import React, { useState } from 'react';
import { Link, generatePath, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import ToDoAlertCard from './ToDoAlertCard';
import { receiveRemoveSiteTodo, removeSiteTodo } from '../../../actions/sites';
import snackbarHelper from '../../../helpers/snackbarHelper';
import OverlayLoader from '../../../components/OverlayLoader';

export const UNCLAIMED_RESCUE = 'Unclaimed Rescue';

const containerSx = {
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  paddingLeft: 2,
  paddingRight: 2,
};

const SDToDoUnclaimedRescuesBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={routes.mobileToDo} label="To-do" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="Unclaimed Rescues" />
    </Breadcrumbs>
  </Box>
);

const ToDoUnclaimedRescues = () => {
  const history = useHistory();
  const location = useLocation();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const [loading, setLoading] = useState();
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const unclaimedRescues = sdTodos?.unclaimedRescues || [];
  const [currentCardIndex, setCurrentCardIndex] = useState(location.state?.toDoCardIndex || 0);

  const handleReviewClick = (todo, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() =>
      dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false))
    )
      .then(() => {
        handleCardOut();
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
          history.push(generatePath(routes.rescue_edit, { rescueId: todo.details.id }), { toDoCardIndex: currentCardIndex });
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const handleSkipClick = () => {
    setCurrentCardIndex(currentCardIndex + 1);
  };

  const handleRemoveClick = (rescue, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() =>
      dispatch(removeSiteTodo(activeSite.id, rescue.entity_id, rescue.entity_type, rescue.type, false))
    )
      .then(() => {
        handleCardOut();
        // Wait for animation to complete before removing todo from the store
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, rescue.entity_id, rescue.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  // Show if there is no more unclaimed rescues to review or when the last card has been skipped
  if (!unclaimedRescues.length || currentCardIndex + 1 > unclaimedRescues.length) {
    return (
      <>
        <SDToDoUnclaimedRescuesBreadcrumbs />
        <Box sx={containerSx}>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              WebkitTransform: 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You’re all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <OverlayLoader loading={loading}>
      <SDToDoUnclaimedRescuesBreadcrumbs />

      <Box sx={containerSx}>
        <Box sx={{ position: 'relative', height: '100%' }}>
          {unclaimedRescues.map((rescue, index) => (
            <ToDoAlertCard
              key={rescue.entity_id}
              rescue={rescue}
              type={UNCLAIMED_RESCUE}
              current={index === currentCardIndex}
              next={unclaimedRescues.length <= index ? null : index === currentCardIndex + 1}
              unclaimed
              onRemoveClick={handleRemoveClick}
              onSkipClick={handleSkipClick}
              onReviewClick={handleReviewClick}
              content={
                <span>
                  Pickup from {}
                  <b>
                    {rescue?.details?.location?.length <= 20
                      ? rescue?.details?.location
                      : `${rescue?.details?.location?.substr(0, 20)}...`}
                  </b>{' '}
                  to {}
                  <b>
                    {rescue?.details?.receiver?.length <= 20 ? rescue?.details?.receiver : `${rescue?.details?.receiver?.substr(0, 20)}...`}
                  </b>{' '}
                  on <b>{moment(rescue?.details?.date).format('ddd MMM Do')}</b> from{' '}
                  <b>{moment(rescue?.details?.pickup_begin, 'HH:mm:ss').format('hh:mma')}</b> to{' '}
                  <b>{moment(rescue?.details?.pickup_end, 'HH:mm:ss').format('hh:mma')}</b>, has no rescuer assigned. Please{' '}
                  assign now!
                </span>
              }
            />
          ))}
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default ToDoUnclaimedRescues;
